import React from 'react';
import Button from '../ui/Button';
import { Link } from 'react-router';
import { SystemAirflowResponse, SystemFilterSlot } from '../../types';
import type { SystemAirflowRefresh } from '../../hooks/useSystemAirflow';
import { WHOLE_HOUSE_FILTER } from '../../utils/clipLabel';

/**
 * FS-593 — how the filters on this HVAC system are laid out, as the app
 * recorded them: a whole-house filter at the furnace, or return grilles, and
 * which Clip (if any) sits in each one.
 *
 * Everything here is the server's: the slot list and each slot's state come
 * from GET /devices/:id/system-airflow, and the grille count is the server's
 * `recorded_grille_count` (null when two records name one Clip, and then no
 * count is shown). Nothing is inferred about the ductwork beyond what the app
 * recorded — a mixed system lists what is on file and stops there.
 */

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** 1-based position of each return grille among the grilles, in stored order. */
export const grilleNumbers = (slots: SystemFilterSlot[]): Map<string, number> => {
  const out = new Map<string, number>();
  let k = 0;
  slots.forEach((slot) => {
    if (slot.type === 'airReturns') out.set(slot.id, (k += 1));
  });
  return out;
};

/** The slot's own name: the homeowner's, else "Grille k" / "Whole-house filter" / "Not recorded". */
export const slotName = (slot: SystemFilterSlot, grilleNumber: number | undefined): string => {
  if (slot.location) return slot.type == null ? `${slot.location} — type not recorded` : slot.location;
  if (slot.type === 'airReturns') return grilleNumber ? `Grille ${grilleNumber}` : 'Return grille';
  // FS-825: the console's one word for the furnace type, as the Device List has it.
  if (slot.type === 'furnace') return WHOLE_HOUSE_FILTER;
  return 'Not recorded';
};

/** What sits in the slot, in the parentheses after its name. */
export const slotOccupant = (slot: SystemFilterSlot, deviceId: number): string => {
  switch (slot.state) {
    case 'member':
      return slot.device_id === deviceId
        ? `Clip ${slot.device_id} — this device`
        : `Clip ${slot.device_id ?? '?'}`;
    case 'none':
      return 'no Clip';
    case 'hidden':
      return 'Clip outside your scope';
    case 'unresolved':
      return 'Clip not on this system';
    case 'conflict':
      // The server withholds the Clip on a conflict it will not name; that
      // says nothing about scope or membership, so neither is claimed.
      return slot.device_id != null
        ? `Clip ${slot.device_id}${slot.device_id === deviceId ? ' — this device' : ''} — conflicting records`
        : 'conflicting records, Clip not shown';
    default:
      return String(slot.state);
  }
};

const STATE_TITLE: Record<SystemFilterSlot['state'], string> = {
  member: 'A Clip on this system sits in this slot.',
  none: 'No Clip is recorded in this slot, so the airflow through it is not measured.',
  hidden: 'A Clip sits in this slot, but it is outside the devices you can see.',
  unresolved:
    'The app names a Clip in this slot that is not on this HVAC system: a stale name, or a Clip that has moved.',
  conflict:
    'More than one filter record names this Clip, so which slot it sits in is not known until the duplicates are cleaned up.',
};

/** The hover text for one slot line: its state, and what is missing from its record. */
export const slotTitle = (slot: SystemFilterSlot, deviceId: number): string => {
  const parts = [
    slot.state === 'member' && slot.device_id === deviceId
      ? "This device's own filter slot."
      : slot.state === 'conflict' && slot.device_id == null
        ? 'More than one filter record names the same Clip, and that Clip is not shown here, so which slot it sits in is not known.'
        : STATE_TITLE[slot.state] ?? `Slot state: ${slot.state}.`,
  ];
  if (slot.state === 'member' && slot.device_id !== deviceId && slot.device_id != null) {
    parts.push(`Open the overview for device #${slot.device_id}.`);
  }
  if (slot.type == null) parts.push('The app has no filter type for this slot.');
  return parts.join(' ');
};

/** The full slot line: "Grille 2 (Clip 243)". */
export const slotLine = (
  slot: SystemFilterSlot,
  grilleNumber: number | undefined,
  deviceId: number,
): string => `${slotName(slot, grilleNumber)} (${slotOccupant(slot, deviceId)})`;

/**
 * The one-line summary. Conflicts come first: while two records name one
 * Clip, the layout is not known, so neither "whole-house" nor a grille count
 * is said — only the conflict, then what the other records are. A conflict
 * whose Clip the server withholds is counted on its own and never merged
 * into "one Clip", since several such groups cannot be told apart. Without
 * a conflict: whole-house when every slot is the furnace filter, otherwise
 * each recorded kind counted.
 */
export const configurationSummary = (data: SystemAirflowResponse): string => {
  const slots = data.slots ?? [];
  if (slots.length === 0) return 'No filter records on this system';

  const conflicted = slots.filter((s) => s.state === 'conflict');
  if (conflicted.length > 0) {
    const byClip = new Map<number, number>();
    let unnamed = 0;
    conflicted.forEach((s) => {
      if (s.device_id != null) byClip.set(s.device_id, (byClip.get(s.device_id) ?? 0) + 1);
      else unnamed += 1;
    });
    const parts: string[] = [];
    byClip.forEach((n, id) => parts.push(`${n} records for Clip ${id} (conflicting)`));
    if (unnamed > 0) {
      parts.push(`${plural(unnamed, 'conflicting record', 'conflicting records')} (Clip not shown)`);
    }
    const others = slots.filter((s) => s.state !== 'conflict');
    const furnaceOthers = others.filter((s) => s.type === 'furnace').length;
    const untypedOthers = others.filter((s) => s.type == null).length;
    if (furnaceOthers > 0) {
      parts.push(plural(furnaceOthers, 'other whole-house filter', 'other whole-house filters'));
    }
    if (untypedOthers > 0) {
      parts.push(plural(untypedOthers, 'other filter record with no type', 'other filter records with no type'));
    }
    return parts.join(', ');
  }

  const furnace = slots.filter((s) => s.type === 'furnace').length;
  if (furnace === slots.length) return 'Whole-house filter (at the furnace)';
  const untyped = slots.filter((s) => s.type == null).length;

  const parts: string[] = [];
  if (data.recorded_grille_count != null && data.recorded_grille_count > 0) {
    parts.push(plural(data.recorded_grille_count, 'recorded return grille', 'recorded return grilles'));
  } else if (data.recorded_grille_count == null && slots.some((s) => s.type === 'airReturns')) {
    // The server withheld the count; say grilles are recorded, not how many.
    parts.push('Return grilles recorded (count not known)');
  }
  if (furnace > 0) parts.push(plural(furnace, 'whole-house filter', 'whole-house filters'));
  if (untyped > 0) parts.push(plural(untyped, 'filter record with no type', 'filter records with no type'));
  return parts.join(', ');
};

/**
 * A slot that keeps the system figure partial, in words, for the airflow
 * block's "Not counted" list: "Hallway grille — no Clip".
 */
export const notCountedSlotText = (slot: SystemFilterSlot, grilleNumber: number | undefined) => {
  let name: string;
  if (slot.type === 'airReturns') {
    name = slot.location ? `${slot.location} grille` : grilleNumber ? `Grille ${grilleNumber}` : 'Return grille';
  } else if (slot.type === 'furnace') {
    name = slot.location ? `${slot.location} whole-house filter` : WHOLE_HOUSE_FILTER;
  } else {
    name = slot.location ? `${slot.location} (type not recorded)` : 'Filter record with no type';
  }
  let why: string;
  if (slot.state === 'none') why = 'no Clip';
  else if (slot.state === 'hidden') why = 'Clip outside your scope';
  else if (slot.state === 'conflict') {
    why =
      slot.device_id != null
        ? `conflicting records for Clip ${slot.device_id}`
        : 'conflicting records, Clip not shown';
  } else why = 'Clip not on this system';
  return `${name} — ${why}`;
};

/** The slot states that keep a system from being complete. */
export const BLOCKING_SLOT_STATES: ReadonlyArray<SystemFilterSlot['state']> = [
  'none',
  'unresolved',
  'hidden',
];

/**
 * Whether a slot belongs in the airflow block's "Not counted" list: a
 * blocking state, or a conflict slot that is not a visible Clip whose
 * estimate went into the figure (a withheld one never is, as far as the
 * page can tell).
 */
export const isNotCountedSlot = (slot: SystemFilterSlot): boolean =>
  BLOCKING_SLOT_STATES.includes(slot.state) ||
  (slot.state === 'conflict' && !(slot.device_id != null && slot.contributed === true));

const SlotItem: React.FC<{
  slot: SystemFilterSlot;
  grilleNumber: number | undefined;
  deviceId: number;
}> = ({ slot, grilleNumber, deviceId }) => {
  const text = slotLine(slot, grilleNumber, deviceId);
  const title = slotTitle(slot, deviceId);
  const linkTo =
    (slot.state === 'member' || slot.state === 'conflict') &&
    slot.device_id != null &&
    slot.device_id !== deviceId
      ? slot.device_id
      : null;
  const tone =
    slot.state === 'member'
      ? ''
      : slot.state === 'conflict' || slot.state === 'unresolved'
        ? 'text-amber-700'
        : 'text-gray-600';
  return (
    <li className={`py-0.5 ${tone}`} title={title} data-testid="filter-slot">
      {linkTo != null ? (
        <Link to={`/devices/${linkTo}/overview`} className="text-link hover:underline" title={title}>
          {text}
        </Link>
      ) : (
        text
      )}
    </li>
  );
};

const Status: React.FC<{ children: React.ReactNode; title?: string; tone?: string }> = ({
  children,
  title,
  tone = 'text-muted-foreground',
}) => (
  <p className={`text-sm ${tone}`} data-testid="filter-configuration-status" title={title}>
    {children}
  </p>
);

const FilterConfiguration: React.FC<{
  data: SystemAirflowResponse | null;
  loading: boolean;
  error: boolean;
  deviceId: number;
  heading?: string;
  headingTitle?: string;
  refresh?: SystemAirflowRefresh;
  onRefresh?: () => void;
}> = ({
  data,
  loading,
  error,
  deviceId,
  heading = 'Filter configuration',
  headingTitle,
  refresh = 'idle',
  onRefresh,
}) => {
  let body: React.ReactNode;
  if (!data) {
    if (loading) {
      body = <Status>Loading the filter configuration…</Status>;
    } else if (error) {
      body = (
        <Status
          tone="text-amber-700"
          title="The device server did not answer, so the filter configuration could not be read."
        >
          Filter configuration unavailable: the server could not be reached.
        </Status>
      );
    } else {
      return null;
    }
  } else if (!data.hvac_id) {
    body = (
      <Status title="The console has no app projection for this Clip yet, so its HVAC system and filters are not known here.">
        Not synced from the app yet
      </Status>
    );
  } else if (!data.coverage) {
    body = (
      <Status title="The app projection for this Clip predates filter locations, or the server does not send them yet. They appear after the next sync.">
        Filter locations not synced yet
      </Status>
    );
  } else {
    const slots = data.slots ?? [];
    const numbers = grilleNumbers(slots);
    body = (
      <>
        <p
          className="text-sm"
          data-testid="filter-configuration-summary"
          title="What the app has recorded for this HVAC system's filters. Nothing is assumed about the ductwork beyond it."
        >
          {configurationSummary(data)}
        </p>
        {slots.length > 0 && (
          <ul className="mt-1 text-sm">
            {slots.map((slot) => (
              <SlotItem
                key={slot.id}
                slot={slot}
                grilleNumber={numbers.get(slot.id)}
                deviceId={deviceId}
              />
            ))}
          </ul>
        )}
      </>
    );
  }

  return (
    <div className="mt-4 border-t pt-4" data-testid="filter-configuration">
      <h4 className="font-semibold text-sm mb-2" title={headingTitle}>
        {heading}
      </h4>
      {body}
      {refresh === 'updating' && (
        <p
          role="status"
          className="mt-1 text-xs text-muted-foreground"
          title="The change was saved. Waiting for the app projection to pick it up; this checks every 3 seconds for up to 30 seconds."
        >
          Updating…
        </p>
      )}
      {refresh === 'gave_up' && (
        <p className="mt-1 text-xs text-amber-700">
          The saved change has not reached this page yet.{' '}
          <Button
            variant="link"
            onClick={onRefresh}
            title="Read the system's filter configuration and airflow again now."
          >
            Refresh
          </Button>
        </p>
      )}
    </div>
  );
};

export default FilterConfiguration;
